<template>
  <tutorial-layout
    title="Popup Notifier"
    :referenceLinks="referenceLinks"
    :youtubeLinks="youtubeLinks"
  >
    <p slot="overview">
      Popup Notifier is used to display a one-liner message. User does not need any third party libraries to show a popup message. It consists of icon, title, description and a close button. All are optional. By default it have 4 types of messages. They are,
      <br/>
      <ul>
        <li>Success</li>
        <li>Error</li>
        <li>Warning</li>
        <li>Information</li>
      </ul>
    </p>

    <p slot="benefits">
      We can easily customize the popup params like icon, color, message, description, delay and position (top, right, bottom, left and center)
    </p>
    
    <h2>How to use this popup notifier ?</h2><br/>

    <v-card>
      <v-card-text>
        <h2>Step 1</h2><br/>
        <p>You have to call the popup notifier using this notification-mixin. You can import this into any component</p>
        <pre>
          <code>&lt;script&gt;
import notificationMixin from "@/_mixins/notification-mixin";
export default {
  mixins: [ notificationMixin ],
}
&lt;/script&gt;</code>
        </pre>
      </v-card-text>
    </v-card><br/>

    <v-card>
      <v-card-text>
        <h2>Step 2<br/><br/>2.1 - Json object</h2><br/>
        <p>Now you can call the popup notifier from any component with the help of mixins. You can call the mixin as follows,</p>
        <pre>
          <code>this.addNotification({
  icon: "mdi-information",
  type: "info", 
  message: "info",
  description: "Sample message",
  delay: 6000,
  dismissable: true,
  top: true,
});</code>
        </pre>
        <h2>2.2 - String</h2><br/>
        <p>Now you can call the popup notifier from any component with the help of mixins. You can call the mixin as follows,</p>
        <pre>
          <code>this.addNotification("Simple Message");</code>
        </pre>
        <v-col>
          <h3>Mixin Description</h3><br/>
          <v-data-table
            :headers="headers"
            :items="mixinDescriptions"
            :items-per-page="100"
            class="elevation-1"
            hide-default-footer
          >
            <template v-slot:item.name="{ item }">
              <div style="color: #7f9a44;">{{ item.name }}</div>
            </template>
            <template v-slot:item.type="{ item }">
              <div style="color: #990055;">{{ item.type }}</div>
            </template>
          </v-data-table>
        </v-col>
        </br>
        <p>For example, let we take <b>HelloWorld.vue</b></p>
        <pre>
          <code>&lt;template&gt;
    &lt;v-btn color="primary" dark @click="info()" class="ma-2"&gt;info&lt;/v-btn&gt;
    &lt;v-btn color="warning" dark @click="warning()" class="ma-2"&gt;Warning&lt;/v-btn&gt;
    &lt;v-btn color="success" dark @click="success()" class="ma-2"&gt;success&lt;/v-btn&gt;
    &lt;v-btn color="error" dark @click="error()" class="ma-2"&gt;error&lt;/v-btn&gt;
    &lt;v-btn color="orange" dark @click="simpleString()" class="ma-2"&gt;Simple String&lt;/v-btn&gt;
  &lt;template&gt;<br/>
&lt;script&gt;
import notificationMixin from "@/_mixins/notification-mixin";
export default {
  name: "HelloWorld",
  mixins: [ notificationMixin ],
  methods: {
    info() {
      this.addNotification({
        icon: "mdi-information",
        type: "info",
        message: "Information message",
        description: null,
        delay: 2000,
      });
    },

    warning() {
      this.addNotification({
        type: "warning",
        message: "Warning",
        description: "System overloaded",
        delay: 5000,
      });
    },

    success() {
      this.addNotification({
        type: "success", 
        message: "Success",
        description: "This message will be closed within 3 seconds",
        delay: 3000,
        dismissable: false,
      });
    },

    error() {
      this.addNotification({
        type: "error", 
        message: "Error",
        description: "This message will be closed within 10 seconds",
        delay: 10000,
      });
    },

    simpleString(){
      this.addNotification("Its a simple string");
    },
  }
}
&lt;/script&gt;</code>
        </pre>
        
        <v-col>
          <h2>Example</h2>
          <br/>
          
          <v-row align="center">
            <v-btn color="primary" dark @click="info()" class="ma-2">info</v-btn>
            <v-btn color="warning" @click="warning()" class="ma-2">warning</v-btn>
            <v-btn color="success" @click="success()" class="ma-2">success</v-btn>
            <v-btn color="error" @click="error()" class="ma-2">error</v-btn>
            <v-btn color="orange" @click="simpleString()" class="ma-2" dark>Simple-String</v-btn>
          </v-row>
          <br/>
          <popup-modal />
        </v-col>
      </v-card-text>
    </v-card><br/>

    <h2>Positions for Popup Notification</h2>
    <br/>
    <v-card>
      <v-card-text>
        <h2>Top</h2>
        <br/>
        <v-row align="center">
          <v-btn color="purple" @click="topInfo()" class="ma-2" dark>Top</v-btn>
          <v-btn color="blue-grey" @click="topLeftInfo()" class="ma-2" dark>Top-Left</v-btn>
          <v-btn color="primary" @click="topRightInfo()" class="ma-2" dark>Top-Right</v-btn>
        </v-row>
        <br/>

        <h2>Centered</h2>
        <br/>
        <v-row align="center">
          <v-btn color="purple" @click="centeredInfo()" class="ma-2" dark>Centered</v-btn>
          <v-btn color="blue-grey" @click="centeredLeftInfo()" class="ma-2" dark>Centered-Left</v-btn>
          <v-btn color="primary" @click="centeredRightInfo()" class="ma-2" dark>Centered-Right</v-btn>
        </v-row>
        <br/>

        <h2>Bottom</h2>
        <br/>
        <v-row align="center">
          <v-btn color="purple" @click="bottomInfo()" class="ma-2" dark>Bottom</v-btn>
          <v-btn color="blue-grey" @click="bottomLeftInfo()" class="ma-2" dark>Bottom-Left</v-btn>
          <v-btn color="primary" @click="bottomRightInfo()" class="ma-2" dark>Bottom-Right</v-btn>
        </v-row>
      </v-card-text>
    </v-card>
  </tutorial-layout>
</template>

<script>
import notificationMixin from "@/_mixins/notification-mixin";
import tutorialLayout from "../../components/tutorial-layout";
import PopupModal from './popup-modal';

export default {
  name: "PopupNotifierExample",
  mixins: [notificationMixin],
  created() {},
  data() {
    return {
      headers: [
        { text: 'Name', value: 'name', sortable: false, },
        { text: 'Type', value: 'type', sortable: false, },
        { text: 'Description', value: 'description', sortable: false, },
      ],
      mixinDescriptions: [
        {
          name: "Icon",
          type: "String",
          description: "Default icon Information | Warning | Success | Error are displayed. You can also change the icon",
        },
        {
          name: "Type",
          type: "String",
          description: "You can set the type of notifier popup such as, info | warning | success | error",
        },
        {
          name: "Message",
          type: "String",
          description: "One line Popup header message displayed here",
        },
        {
          name: "Description",
          type: "String",
          description: "Additional description can also be added to this notification",
        },
        {
          name: "Dismissable",
          type: "String",
          description: "Used to set the close button. User can manually close the notification",
        },
        {
          name: "Delay",
          type: "Number",
          description: "Sets the notification to be in idol (in seconds)",
        },
        {
          name: "Top",
          type: "Boolean",
          description: "Placed the notification in the top of the page",
        },
        {
          name: "Bottom",
          type: "Boolean",
          description: "Default position is bottom. Placed the notification in the bottom of the page",
        },
        {
          name: "Centered",
          type: "Boolean",
          description: "Placed the notification in the center of the page",
        },
        {
          name: "Left",
          type: "Boolean",
          description: "By default it places the notification in the bottom left of the page",
        },
        {
          name: "Right",
          type: "Boolean",
          description: "By default it places the notification in the bottom right of the page",
        },
      ],
    }
  },
  components: { tutorialLayout, PopupModal },
  props: {},
   watch: {},
  computed: {
    referenceLinks() {
      return [];
    },
    youtubeLinks() {
      return [];
    },
  },
  methods: {
    info() {
      this.addNotification({
        icon: "mdi-information",
        type: "info", //info, warning, success, error
        message: "Information message",
        description: null,
        delay: 2000, //milliseconds min: 1000, max 60000,
      });
    },

    warning() {
      this.addNotification({
        type: "warning",
        message: "Warning",
        description: "System overloaded",
        delay: 5000,
      });
    },

    success() {
      this.addNotification({
        type: "success", 
        message: "Success",
        description: "This message will be closed within 3 seconds",
        delay: 3000,
        dismissable: false,
      });
    },

    error() {
      this.addNotification({
        type: "error", 
        message: "Error",
        description: "This message will be closed within 10 seconds",
        delay: 10000,
      });
    },

    simpleString(){
      this.addNotification("Its a simple string");
    },

    topInfo(){
      this.addNotification({
        type: "purple", 
        message: "Top",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
        top: true,
      });
    },

     topLeftInfo(){
      this.addNotification({
        type: "blue-grey", 
        message: "Top-Left",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
        top: true,
        left: true,
      });
    },

     topRightInfo(){
      this.addNotification({
        type: "primary", 
        message: "Top-Right",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
        right: true,
        top: true,
      });
    },

     centeredInfo(){
      this.addNotification({
        type: "purple", 
        message: "Centered",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
        centered: true,
      });
    },

     centeredLeftInfo(){
      this.addNotification({
        type: "blue-grey", 
        message: "Centered-Left",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
        centered: true,
        left: true,
      });
    },

     centeredRightInfo(){
      this.addNotification({
        type: "primary", 
        message: "Centered-Right",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
        right: true,
        centered: true,
      });
    },

    bottomInfo(){
      this.addNotification({
        type: "purple", 
        message: "Bottom",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
      });
    },

     bottomLeftInfo(){
      this.addNotification({
        type: "blue-grey", 
        message: "Bottom-Left",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
        left: true,
      });
    },

     bottomRightInfo(){
      this.addNotification({
        type: "primary", 
        message: "Bottom-Right",
        description: "You have to close this notification manually",
        delay: -1,
        dismissable: true,
        right: true,
      });
    },

  },
};
</script>
<style lang="scss">
</style>
